import { buildCsrfHeader, hasCsrfToken } from "./csrf";

// Vite exposes env vars through `import.meta.env`.
// We allow an override via VITE_API_BASE_URL and default to local Spring Boot.
// `.replace(/\/$/, "")` removes one trailing slash so:
// "http://localhost:8080/" + "/api/auth/me" does not become "//api/auth/me".
export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, "") ?? "http://localhost:8080";

// Supported HTTP methods for this app's.
//Mainly guardrail for correctness type safety.
type ApiMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

// Normalized error shape thrown by apiRequest when response.ok === false.
// Example:
// status: 401
// payload: { "message": "Invalid email or password" } or plain text.
export class ApiError extends Error {
  readonly status: number;
  readonly payload: unknown;

  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }
}

export type ApiRequestOptions = Omit<RequestInit, "method" | "body"> & {
  // Defaults to GET when omitted.
  method?: ApiMethod;
  // Accepts JSON-compatible object/primitive or FormData.
  body?: unknown;
  // Escape hatch for calls that intentionally skip CSRF header injection.
  skipCsrf?: boolean;
};

// CSRF is only needed for state-changing methods.
function isMutation(method: ApiMethod): boolean {
  return method === "POST" || method === "PUT" || method === "PATCH" || method === "DELETE";
}

// Attempts to extract useful error details from failed responses.
// If server returns JSON -> parse JSON.
// Otherwise -> parse text.
// If parsing fails -> null.
async function parseErrorPayload(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type") ?? "";

  if (contentType.includes("application/json") || contentType.includes("+json")) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  try {
    return await response.text();
  } catch {
    return null;
  }
}

// Builds final request headers by combining caller headers + app defaults.
function buildHeaders(options: ApiRequestOptions, method: ApiMethod): Headers {
  // Start with any caller-provided headers so we can preserve overrides.
  const headers = new Headers(options.headers ?? {});

  // Default expected response format for most API endpoints.
  // We only set it if missing so callers can request something else (csv/pdf/etc).
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }

  // Determine whether request has a body and whether it is FormData.
  // FormData must not get JSON content-type because browser sets multipart boundary.
  const hasBody = options.body !== undefined && options.body !== null;
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;

  // For non-FormData bodies, default to JSON.
  if (hasBody && !isFormData && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // Add CSRF header only when:
  // 1) caller did not opt out
  // 2) request mutates server state
  // 3) CSRF cookie exists
  // Example added header: { "X-XSRF-TOKEN": "abc123==" }
  if (!options.skipCsrf && isMutation(method) && hasCsrfToken()) {
    const csrfHeader = buildCsrfHeader();
    for (const [key, value] of Object.entries(csrfHeader)) {
      headers.set(key, value);
    }
  }

  return headers;
}

const JSON_BODY_ERROR_MESSAGE =
  "apiRequest body must be JSON-compatible data. Pass a plain payload object instead of a browser event, DOM element, or circular object.";

function isDomElement(value: unknown): boolean {
  return typeof Element !== "undefined" && value instanceof Element;
}

function isBrowserEvent(value: unknown): boolean {
  return typeof Event !== "undefined" && value instanceof Event;
}

function isReactEventLike(value: unknown): boolean {
  if (value === null || typeof value !== "object") {
    return false;
  }

  const maybeEvent = value as {
    currentTarget?: unknown;
    nativeEvent?: unknown;
    target?: unknown;
  };

  return isDomElement(maybeEvent.currentTarget) || isDomElement(maybeEvent.target) || isBrowserEvent(maybeEvent.nativeEvent);
}

function serializeJsonBody(body: unknown): string {
  if (isDomElement(body) || isBrowserEvent(body) || isReactEventLike(body)) {
    throw new TypeError(JSON_BODY_ERROR_MESSAGE);
  }

  try {
    const serializedBody = JSON.stringify(body);
    if (serializedBody === undefined) {
      throw new TypeError(JSON_BODY_ERROR_MESSAGE);
    }

    return serializedBody;
  } catch (error) {
    if (error instanceof TypeError && error.message === JSON_BODY_ERROR_MESSAGE) {
      throw error;
    }

    throw new TypeError(JSON_BODY_ERROR_MESSAGE);
  }
}

// Shared API function used by feature modules.
// Example usage:
// apiRequest<AuthUserResponseDto>("/api/auth/me")
export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  // GET is the default method when none is provided.
  const method = options.method ?? "GET";
  const headers = buildHeaders(options, method);
  const bodyIsFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  const body =
    // No-body calls should send no body.
    options.body === undefined || options.body === null
      ? undefined
      // Pass FormData through untouched.
      : bodyIsFormData
        ? (options.body as FormData)
        // Everything else is JSON-serialized.
        // Example: { email: "a@b.com", password: "secret" } -> "{\"email\":\"a@b.com\",\"password\":\"secret\"}"
        : serializeJsonBody(options.body);

  // Build final absolute URL: `${API_BASE_URL}${path}`
  // Example: "http://localhost:8080" + "/api/auth/me"
  const response = await fetch(`${API_BASE_URL}${path}`, {
    // Keep caller options (signal, mode, cache, etc).
    ...options,
    method,
    headers,
    // Required for session-cookie authentication (JSESSIONID).
    credentials: "include",
    body,
  });

  // Normalize any non-2xx/3xx response into ApiError.
  if (!response.ok) {
    const payload = await parseErrorPayload(response);
    throw new ApiError(`Request failed with status ${response.status}`, response.status, payload);
  }

  // No-content success endpoint (common for logout/delete).
  // Example: HTTP 204 -> returns `undefined`.
  if (response.status === 204) {
    return undefined as T;
  }

  // If response is not JSON, return undefined.
  // This keeps helper safe for endpoints that return empty or non-JSON bodies.
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    return undefined as T;
  }

  // Parse and return typed JSON response.
  // Example: returns AuthUserResponseDto for /api/auth/me.
  return (await response.json()) as T;
}
